import * as React from 'react';
import { Toaster } from '@/components/ui/sonner';

type Theme = 'light' | 'dark' | 'system';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const THEME_STORAGE_KEY = 'theme';
const THEME_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = React.createContext<{
  theme: Theme;
  setTheme: React.Dispatch<React.SetStateAction<Theme>>;
}>({
  theme: 'light',
  setTheme: () => {},
});

function getPreferredTheme(): Theme {
  return 'light';
}

function resolveTheme(theme: Theme) {
  return 'light';
}

function applyTheme(theme: Theme) {
  const root = window.document.documentElement;
  const resolvedTheme = resolveTheme(theme);

  root.classList.toggle('dark', resolvedTheme === 'dark');
  root.style.colorScheme = resolvedTheme;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = React.useState<Theme>(getPreferredTheme);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    applyTheme('light');
    window.localStorage.setItem(THEME_STORAGE_KEY, 'light');
  }, [theme]);

  const handleSetTheme = React.useCallback<React.Dispatch<React.SetStateAction<Theme>>>((value) => {
    setTheme(() => {
      if (typeof value === 'function') {
        value('light');
      }
      return 'light';
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme: 'light', setTheme: handleSetTheme }}>
      {children}
      <Toaster position="top-right" richColors closeButton />
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return React.useContext(ThemeContext);
}
